import { useState, useRef, useEffect, useCallback } from 'react';
import { useSizeGuide } from '../hooks/usePublicContent';

const API_URL = import.meta.env.VITE_API_URL as string;

interface SizeGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SizeGuideModal({ isOpen, onClose }: SizeGuideModalProps) {
  const { data: sizeGuide } = useSizeGuide();
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const imageSrc = sizeGuide?.image_path ? `${API_URL}${sizeGuide.image_path}` : null;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIsLightboxOpen(false);
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isLightboxOpen) {
          setIsLightboxOpen(false);
          setScale(1);
          setPosition({ x: 0, y: 0 });
        } else if (isOpen) {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLightboxOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setScale((s) => Math.min(s + 0.5, 4));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((s) => {
      const newScale = Math.max(s - 0.5, 1);
      if (newScale === 1) setPosition({ x: 0, y: 0 });
      return newScale;
    });
  }, []);

  const handleResetZoom = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Mouse drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers for pinch-to-zoom
  const lastTouchDistance = useRef<number | null>(null);
  const lastTouchCenter = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouchDistance.current = Math.sqrt(dx * dx + dy * dy);
      lastTouchCenter.current = {
        x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
      };
    } else if (e.touches.length === 1 && scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && lastTouchDistance.current !== null) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const scaleDelta = distance / lastTouchDistance.current;
      
      setScale((s) => Math.min(Math.max(s * scaleDelta, 1), 4));
      lastTouchDistance.current = distance;
    } else if (e.touches.length === 1 && isDragging && scale > 1) {
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y,
      });
    }
  };

  const handleTouchEnd = () => {
    lastTouchDistance.current = null;
    lastTouchCenter.current = null;
    setIsDragging(false);
    if (scale <= 1) {
      setPosition({ x: 0, y: 0 });
    }
  };

  // Wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    if (isLightboxOpen) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.2 : 0.2;
      setScale((s) => {
        const newScale = Math.min(Math.max(s + delta, 1), 4);
        if (newScale === 1) setPosition({ x: 0, y: 0 });
        return newScale;
      });
    }
  };

  if (!isOpen || !imageSrc) return null;

  return (
    <>
      {/* Modal backdrop */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4 md:p-8 animate-fadeIn"
        onClick={onClose}
      >
        {/* Modal content */}
        <div
          className="relative w-full max-w-4xl max-h-[90vh] bg-cream rounded-2xl shadow-2xl overflow-hidden animate-scaleIn"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-stone-light px-6 py-4">
            <div>
              <h2 className="font-serif text-2xl font-light text-ink">Size Guide</h2>
              <p className="mt-0.5 text-sm text-stone">Click the image to view full screen</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-full text-stone transition-colors hover:bg-stone-light/50 hover:text-ink"
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Image container */}
          <div
            className="relative overflow-auto bg-paper"
            style={{ maxHeight: 'calc(90vh - 80px)' }}
          >
            <img
              ref={imageRef}
              src={imageSrc}
              alt="Size Guide"
              className="mx-auto w-full h-auto cursor-zoom-in select-none"
              onClick={() => setIsLightboxOpen(true)}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              style={{ 
                WebkitUserSelect: 'none',
                userSelect: 'none',
                pointerEvents: 'auto',
              }}
            />
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {isLightboxOpen && (
        <div
          ref={containerRef}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/95 animate-fadeIn"
          onClick={() => {
            if (scale === 1) {
              setIsLightboxOpen(false);
            }
          }}
          onWheel={handleWheel}
        >
          {/* Controls */}
          <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
            <button
              type="button"
              onClick={handleZoomOut}
              disabled={scale <= 1}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-cream/10 text-cream backdrop-blur-sm transition-all hover:bg-cream/20 disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Zoom out"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM13.5 10.5h-6" />
              </svg>
            </button>
            <span className="min-w-[3rem] text-center text-sm font-medium text-cream/80">
              {Math.round(scale * 100)}%
            </span>
            <button
              type="button"
              onClick={handleZoomIn}
              disabled={scale >= 4}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-cream/10 text-cream backdrop-blur-sm transition-all hover:bg-cream/20 disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Zoom in"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" />
              </svg>
            </button>
            {scale > 1 && (
              <button
                type="button"
                onClick={handleResetZoom}
                className="ml-2 flex h-10 items-center gap-1.5 rounded-full bg-cream/10 px-4 text-sm font-medium text-cream backdrop-blur-sm transition-all hover:bg-cream/20"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setIsLightboxOpen(false);
                setScale(1);
                setPosition({ x: 0, y: 0 });
              }}
              className="ml-2 flex h-10 w-10 items-center justify-center rounded-full bg-cream/10 text-cream backdrop-blur-sm transition-all hover:bg-cream/20"
              aria-label="Close lightbox"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Zoom hint */}
          {scale === 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-full bg-cream/10 px-4 py-2 text-sm text-cream/70 backdrop-blur-sm">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" />
              </svg>
              <span>Scroll to zoom · Pinch on mobile</span>
            </div>
          )}

          {/* Image */}
          <div
            className="flex items-center justify-center overflow-hidden"
            style={{
              width: '100%',
              height: '100%',
              cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-out',
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <img
              src={imageSrc}
              alt="Size Guide"
              className="max-h-[85vh] max-w-[90vw] select-none rounded-lg shadow-2xl transition-transform duration-150"
              style={{
                transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                WebkitUserSelect: 'none',
                userSelect: 'none',
              }}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                if (scale === 1) {
                  handleZoomIn();
                }
              }}
            />
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-scaleIn {
          animation: scaleIn 0.25s ease-out;
        }
      `}</style>
    </>
  );
}

// Size Guide Button component for reuse
interface SizeGuideButtonProps {
  className?: string;
}

export function SizeGuideButton({ className = '' }: SizeGuideButtonProps) {
  const { data: sizeGuide, isLoading } = useSizeGuide();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Don't render if no image or still loading
  if (isLoading || !sizeGuide?.image_path) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className={`inline-flex items-center gap-2 rounded-lg border border-stone-light bg-cream px-4 py-2.5 text-sm font-medium text-ink shadow-sm transition-all hover:border-stone hover:bg-white hover:shadow-md ${className}`}
      >
        <span className="text-base">📏</span>
        <span>Size Guide</span>
      </button>
      <SizeGuideModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}
